import { Injectable, Type } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    ApplicationConfigService,
    ComponentType,
    ExtendedBaseProfileField,
    FACES_FIELD_DEFINITION_DOTTED_KEY,
    FieldType,
    OverrideDescriptor,
    ShBaseGenericInputFieldComponent,
    ShBaseGenericReadonlyFieldComponent,
    ShBooleanInputFieldComponent,
    ShBooleanReadonlyFieldComponent,
    ShDateInputFieldComponent,
    ShDateReadonlyFieldComponent,
    ShEDTFInputFieldComponent,
    ShEnumInputFieldComponent,
    ShEnumReadonlyFieldComponent,
    ShFacesListInputFieldComponent,
    ShFacesListReadonlyFieldComponent,
    ShFramesInputFieldComponent,
    ShFramesReadonlyFieldComponent,
    ShLabeledUrlReadonlyFieldComponent,
    ShLargeTextInputFieldComponent,
    ShLargeTextReadonlyFieldComponent,
    ShListInputFieldComponent,
    ShListReadonlyFieldComponent,
    ShLongInputFieldComponent,
    ShLongReadonlyFieldComponent,
    ShMapInputFieldComponent,
    ShMapReadonlyFieldComponent,
    ShMultiItemInputFieldComponent,
    ShMultiItemReadonlyFieldComponent,
    ShProfileConfigService,
    ShSimpleInputFieldComponent,
    ShSimpleReadonlyFieldComponent,
    ShTimeCodeInputFieldComponent,
    ShTimeCodeReadonlyFieldComponent,
    ShUrlInputFieldComponent,
    ShUrlReadonlyFieldComponent,
    UserFunction,
    UserService
} from '@stylehaven/components';
import { AddressInputFieldComponent } from '../../../generic-form-extensions/components/input/address-input-field/address-input-field.component';
import { ComplexListInputFieldComponent } from '../../../generic-form-extensions/components/input/complex-list-input-field/complex-list-input-field.component';
import { JdnInputFieldComponent } from '../../../generic-form-extensions/components/input/jdn-input-field/jdn-input-field.component';
import { PermissionsInputFieldComponent } from '../../../generic-form-extensions/components/input/permissions-input-field/permissions-input-field.component';
import { TagInputFieldComponent } from '../../../generic-form-extensions/components/input/tag-input-field/tag-input-field.component';
import { ThesaurusInputFieldComponent } from '../../../generic-form-extensions/components/input/thesaurus-input-field/thesaurus-input-field.component';
import { WebserviceListInputFieldComponent } from '../../../generic-form-extensions/components/input/webservice-list-input-field/webservice-list-input-field.component';
import { AddressReadonlyFieldComponent } from '../../../generic-form-extensions/components/readonly/address-readonly-field/address-readonly-field.component';
import { ComplexListReadonlyFieldComponent } from '../../../generic-form-extensions/components/readonly/complex-list-readonly-field/complex-list-readonly-field.component';
import { PermissionsReadonlyFieldComponent } from '../../../generic-form-extensions/components/readonly/permissions-readonly-field/permissions-readonly-field.component';
import { ThesaurusReadonlyFieldComponent } from '../../../generic-form-extensions/components/readonly/thesaurus-readonly-field/thesaurus-readonly-field.component';
import { WebserviceListReadonlyFieldComponent } from '../../../generic-form-extensions/components/readonly/webservice-list-readonly-field/webservice-list-readonly-field.component';

@Injectable({ providedIn: 'root' })
export class MhProfileConfigService extends ShProfileConfigService {
    public overrideInputMapping: Record<ComponentType, Type<ShBaseGenericInputFieldComponent>> = {
        [ComponentType.Address]: AddressInputFieldComponent,
        [ComponentType.AiFaces]: ShFacesListInputFieldComponent,
        [ComponentType.Boolean]: ShBooleanInputFieldComponent,
        [ComponentType.ComplexList]: ComplexListInputFieldComponent,
        [ComponentType.Date]: ShDateInputFieldComponent,
        [ComponentType.Edtf]: ShEDTFInputFieldComponent,
        [ComponentType.Enum]: ShEnumInputFieldComponent,
        [ComponentType.Frames]: ShFramesInputFieldComponent,
        [ComponentType.Jdn]: JdnInputFieldComponent,
        [ComponentType.LabeledUrl]: ShMapInputFieldComponent,
        [ComponentType.List]: ShListInputFieldComponent,
        [ComponentType.MapField]: ShMapInputFieldComponent,
        [ComponentType.MultiItem]: ShMultiItemInputFieldComponent,
        [ComponentType.Number]: ShLongInputFieldComponent,
        [ComponentType.Permissions]: PermissionsInputFieldComponent,
        [ComponentType.Tags]: TagInputFieldComponent,
        [ComponentType.Text]: ShSimpleInputFieldComponent,
        [ComponentType.TextArea]: ShLargeTextInputFieldComponent,
        [ComponentType.Thesaurus]: ThesaurusInputFieldComponent,
        [ComponentType.TimeCode]: ShTimeCodeInputFieldComponent,
        [ComponentType.Url]: ShUrlInputFieldComponent,
        [ComponentType.WebserviceList]: WebserviceListInputFieldComponent
    };

    public overrideReadonlyMapping: Record<ComponentType, Type<ShBaseGenericReadonlyFieldComponent>> = {
        [ComponentType.Address]: AddressReadonlyFieldComponent,
        [ComponentType.AiFaces]: ShFacesListReadonlyFieldComponent,
        [ComponentType.Boolean]: ShBooleanReadonlyFieldComponent,
        [ComponentType.ComplexList]: ComplexListReadonlyFieldComponent,
        [ComponentType.Date]: ShDateReadonlyFieldComponent,
        [ComponentType.Edtf]: ShEnumReadonlyFieldComponent,
        [ComponentType.Enum]: ShEnumReadonlyFieldComponent,
        [ComponentType.Frames]: ShFramesReadonlyFieldComponent,
        [ComponentType.Jdn]: ShSimpleReadonlyFieldComponent,
        [ComponentType.LabeledUrl]: ShLabeledUrlReadonlyFieldComponent,
        [ComponentType.List]: ShListReadonlyFieldComponent,
        [ComponentType.MapField]: ShMapReadonlyFieldComponent,
        [ComponentType.MultiItem]: ShMultiItemReadonlyFieldComponent,
        [ComponentType.Number]: ShLongReadonlyFieldComponent,
        [ComponentType.Permissions]: PermissionsReadonlyFieldComponent,
        [ComponentType.Tags]: ShListReadonlyFieldComponent,
        [ComponentType.Text]: ShSimpleReadonlyFieldComponent,
        [ComponentType.TextArea]: ShLargeTextReadonlyFieldComponent,
        [ComponentType.Thesaurus]: ThesaurusReadonlyFieldComponent,
        [ComponentType.TimeCode]: ShTimeCodeReadonlyFieldComponent,
        [ComponentType.Url]: ShUrlReadonlyFieldComponent,
        [ComponentType.WebserviceList]: WebserviceListReadonlyFieldComponent
    };

    constructor(
        private readonly configService: ApplicationConfigService,
        private readonly userService: UserService
    ) {
        super();
    }

    public getDefaultInputMappings(): Record<FieldType, Type<ShBaseGenericInputFieldComponent>> {
        return {
            ...this.defaultInputMapping,
            [FieldType.ComplexListField]: ComplexListInputFieldComponent,
            [FieldType.PositionField]: AddressInputFieldComponent,
            [FieldType.ThesaurusField]: ThesaurusInputFieldComponent
        };
    }

    public getInputOverridesConfig(): Observable<Record<string, OverrideDescriptor>> {
        return this.configService.getConfig().pipe(
            map(config => {
                const defaultOverrides: Record<string, OverrideDescriptor> = {
                    [FACES_FIELD_DEFINITION_DOTTED_KEY]: { type: ComponentType.AiFaces },
                    'RightsManagement.Permissions': { type: ComponentType.Permissions },
                    'Descriptive.Keywords': {
                        type: ComponentType.Tags,
                        config: {
                            aiSuggestionsEnabled: config.enableAiKeywordSuggestions,
                            aiSuggestionsField: 'Dynamic.Mh2AiKeywords'
                        }
                    }
                };
                return defaultOverrides;
            })
        );
    }

    public getDefaultReadOnlyMappings(): Record<FieldType, Type<ShBaseGenericReadonlyFieldComponent>> {
        return {
            ...this.defaultReadonlyMapping,
            [FieldType.ComplexListField]: ComplexListReadonlyFieldComponent,
            [FieldType.PositionField]: AddressReadonlyFieldComponent,
            [FieldType.ThesaurusField]: ThesaurusReadonlyFieldComponent
        };
    }

    public getReadonlyOverridesConfig(): Observable<Record<string, OverrideDescriptor>> {
        return of({
            [FACES_FIELD_DEFINITION_DOTTED_KEY]: { type: ComponentType.AiFaces },
            'RightsManagement.Permissions': { type: ComponentType.Permissions }
        });
    }

    public isVisuallyHidden(field: ExtendedBaseProfileField, formGroup: UntypedFormGroup): boolean {
        // Currently only setup to hide Mh2Reuse field for specific values of Mh2Gdpr
        const hidden: boolean =
            field.FieldDefinition.DottedKey === 'Dynamic.Mh2Reuse' &&
            formGroup.value.hasOwnProperty('Dynamic.Mh2Gdpr') &&
            formGroup.value.hasOwnProperty('Dynamic.Mh2Reuse') &&
            this.formGroupHasValue(formGroup, 'Dynamic.Mh2Gdpr', 'Disallowed');

        return hidden;
    }

    public override canViewStandard(): Observable<boolean> {
        return this.userService.hasUserFunction(UserFunction.viewStandardFields);
    }

    public override canEditStandard(): Observable<boolean> {
        return this.userService.hasUserFunction(UserFunction.editStandardFields);
    }

    public override canViewAdvanced(): Observable<boolean> {
        return this.userService.hasUserFunction(UserFunction.viewAdvancedFields);
    }

    public override canEditAdvanced(): Observable<boolean> {
        return this.userService.hasUserFunction(UserFunction.editAdvancedFields);
    }
}
